<template>
  <div class="apply-table">
    <!-- 데스크탑/태블릿 ≥640px: 테이블 -->
    <DataTable
      v-if="!isMobile"
      :headers="headers"
      :items="rows"
      :items-per-page="10"
      density="comfortable"
      row-key="_id"
    >
      <template #item.no="{ index }">
        <span class="tabular-nums">{{ index + 1 }}</span>
      </template>

      <template #item.applyTime="{ item }">
        <span class="tabular-nums mono">{{ item.applyTime }}</span>
      </template>

      <template #item.tel="{ item }">
        <span class="tabular-nums mono">{{ formatTel(item.tel) }}</span>
      </template>

      <template #item.detail="{ item, index }">
        <div class="detail-cell" ref="clickOutside">
          <button class="btn-ghost-sm" @click="toggleDetail(index + 1)">
            {{ item.transferDetail.length }} 건
          </button>
          <div v-if="detailVisible === index + 1" class="popup" @click.stop>
            <p
              v-for="(d, di) in item.transferDetail"
              :key="di"
              class="popup__row"
            >
              {{ d.name }} - {{ d.grade }}학년 {{ d.classNum }}반 {{ d.attendanceNumber }}번
            </p>
          </div>
        </div>
      </template>

      <template #item.historyLength="{ item }">
        <span class="tabular-nums">{{ item.historyLength }}건</span>
      </template>

      <template #item.issue="{ item }">
        <button
          class="btn-primary-sm"
          @click="onClickIssue(item._id, item.transferDetail.length, item.tel)"
        >
          발급
        </button>
      </template>

      <template #item.refuse="{ item }">
        <button
          class="btn-destructive-sm"
          @click="openModal(item)"
          aria-label="반려"
        >
          반려
        </button>
      </template>

      <template #empty>
        <EmptyState
          title="처리 대기 중인 신청이 없습니다"
          description="새로운 전학생 발급 신청이 접수되면 여기에 표시됩니다."
        />
      </template>
    </DataTable>

    <!-- 모바일 ≤640px: 카드 리스트 -->
    <div v-else class="mobile-cards">
      <div v-if="rows.length === 0" class="mobile-empty">
        <EmptyState
          title="처리 대기 중인 신청이 없습니다"
          description="새로운 전학생 발급 신청이 접수되면 여기에 표시됩니다."
        />
      </div>
      <article
        v-for="(item, index) in rows"
        :key="item._id || index"
        class="apply-card"
      >
        <header class="apply-card__head">
          <div class="apply-card__avatar" aria-hidden="true">
            <v-icon icon="mdi-school" size="20" />
          </div>
          <div class="apply-card__heading">
            <h3 class="apply-card__school">{{ item.schoolName }}</h3>
            <p class="apply-card__meta">
              <span class="tabular-nums mono">{{ item.applyTime }}</span>
              <span class="apply-card__dot">·</span>
              <span>신청 #{{ index + 1 }}</span>
            </p>
          </div>
          <span class="apply-card__status">
            <span class="apply-card__status-dot" />
            처리 대기
          </span>
        </header>

        <div class="apply-card__contact">
          <div class="apply-card__contact-item">
            <v-icon icon="mdi-account-outline" size="16" class="apply-card__contact-icon" />
            <span>{{ item.teacherName }}</span>
          </div>
          <div class="apply-card__contact-item">
            <v-icon icon="mdi-phone-outline" size="16" class="apply-card__contact-icon" />
            <span class="tabular-nums mono">{{ formatTel(item.tel) }}</span>
          </div>
        </div>

        <div class="apply-card__reason">
          <div class="apply-card__reason-label">신청 사유</div>
          <p class="apply-card__reason-text">{{ item.reason }}</p>
        </div>

        <div class="apply-card__chips">
          <div class="detail-cell" ref="clickOutside">
            <button class="apply-card__chip apply-card__chip--button" @click="toggleDetail(index + 1)">
              <v-icon icon="mdi-account-multiple-outline" size="14" />
              학생 {{ item.transferDetail.length }}건
              <v-icon icon="mdi-chevron-down" size="14" />
            </button>
            <div v-if="detailVisible === index + 1" class="popup popup--mobile" @click.stop>
              <p
                v-for="(d, di) in item.transferDetail"
                :key="di"
                class="popup__row"
              >
                {{ d.name }} - {{ d.grade }}학년 {{ d.classNum }}반 {{ d.attendanceNumber }}번
              </p>
            </div>
          </div>
          <div class="apply-card__chip">
            <v-icon icon="mdi-history" size="14" />
            기존 처리 {{ item.historyLength }}건
          </div>
        </div>

        <footer class="apply-card__footer">
          <button
            class="apply-card__action apply-card__action--primary"
            @click="onClickIssue(item._id, item.transferDetail.length, item.tel)"
          >
            <v-icon icon="mdi-check" size="16" />
            발급 처리
          </button>
          <button
            class="apply-card__action apply-card__action--reject"
            @click="openModal(item)"
            aria-label="반려"
          >
            <v-icon icon="mdi-close" size="16" />
            반려
          </button>
        </footer>
      </article>
    </div>

    <div v-if="pagelength" class="pagination-row">
      <v-pagination
        v-model="currentPage"
        :length="pagelength"
        :total-visible="7"
      />
    </div>

    <!-- 반려 사유 모달 -->
    <div v-if="modalVisible" class="modal-backdrop" @click.self="closeModal">
      <div class="modal-panel" role="dialog" aria-modal="true">
        <header class="modal-panel__header">
          <h2 class="modal-panel__title">반려 사유 입력</h2>
          <button class="modal-close" @click="closeModal" aria-label="닫기">✖</button>
        </header>
        <div class="modal-panel__body">
          <label class="form-label">반려 사유 <span class="required">(필수)</span></label>
          <textarea
            v-model="returnValue"
            class="modal-textarea"
            placeholder="반려 사유를 입력하세요"
            spellcheck="false"
          />
          <p class="modal-helper">신청자에게 반려 사유 문자가 전송됩니다.</p>
        </div>
        <footer class="modal-panel__footer">
          <button class="btn-secondary" @click="closeModal">취소</button>
          <button class="btn-destructive" @click="onConfirmRefuse">반려 확정</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useStore } from "vuex";
import {
  gettransferstudentlength,
  gettransferstudentlist,
  getprocesshistory,
  transferapplysms,
} from "@/api/user-api-list";
import { onClickOutside } from "@vueuse/core";
import DataTable from "@/components/ui/DataTable.vue";
import EmptyState from "@/components/ui/EmptyState.vue";

const store = useStore();
const detailVisible = ref(null);
const clickOutside = ref(null);
const modalVisible = ref(false);
const returnValue = ref("");
const pagelength = ref(null);
const currentPage = ref(1);
const rows = ref([]);
const activeTarget = ref(null);
const windowWidth = ref(typeof window !== "undefined" ? window.innerWidth : 1024);
const isMobile = computed(() => windowWidth.value <= 640);
const onResize = () => { windowWidth.value = window.innerWidth; };

const headers = [
  { title: "No", key: "no", sortable: false, width: 56 },
  { title: "신청 일시", key: "applyTime", sortable: false },
  { title: "학교이름", key: "schoolName", sortable: false },
  { title: "교사이름", key: "teacherName", sortable: false },
  { title: "연락처", key: "tel", sortable: false },
  { title: "신청사유", key: "reason", sortable: false },
  { title: "신청 상세", key: "detail", sortable: false, width: 100 },
  { title: "기존 처리", key: "historyLength", sortable: false, width: 100 },
  { title: "발급", key: "issue", sortable: false, width: 96, align: "center" },
  { title: "반려", key: "refuse", sortable: false, width: 96, align: "center" },
];

const formatTel = (tel) => {
  if (!tel) return "";
  return String(tel).replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
};

const toggleDetail = (index) => {
  detailVisible.value = detailVisible.value === index ? null : index;
};

onClickOutside(clickOutside, () => {
  detailVisible.value = null;
});

const openModal = (item) => {
  activeTarget.value = item;
  returnValue.value = "";
  modalVisible.value = true;
};

const closeModal = () => {
  modalVisible.value = false;
  activeTarget.value = null;
};

const getPageLength = async () => {
  try {
    const response = await gettransferstudentlength();
    if (!response.data.data) {
      pagelength.value = 1;
    } else {
      pagelength.value = response.data.data;
    }
  } catch (err) {
    console.log(err);
  }
};

const getTransferStudentsList = async (page) => {
  try {
    const response = await gettransferstudentlist(page);
    if (response.data.data && response.data.data.length > 0) {
      const data = response.data.data;
      for (let i = 0; i < data.length; i++) {
        const res = await getprocesshistory(data[i].schoolCode);
        data[i].historyLength = res.data.data;
      }
      rows.value = data;
    } else {
      rows.value = [];
    }
  } catch (error) {
    console.error("Error fetching transfer student list:", error);
  }
};

const onClickIssue = async (_id, count, tel) => {
  try {
    const data = {
      _id,
      processStatus: true,
      refuseReason: "",
    };

    const response = await store.dispatch("POST_TRANSFER_APPLY", data);

    if (response) {
      const res = await transferapplysms({ phonenum: tel, status: true });
      if (res.data.data.message == "good") {
        alert(
          `계정 ${count}개의 발급 및 전송이 완료되었습니다.\n\n문자 건수: 1건\nLMS 건수: 1건`
        );
        window.location.reload(true);
      } else {
        alert("API 요청이 성공했지만 서버로부터 올바른 응답을 받지 못했습니다.");
      }
    }
  } catch (err) {
    alert("API 요청이 실패했습니다.");
  }
};

const onConfirmRefuse = async () => {
  if (!activeTarget.value) return;
  const target = activeTarget.value;
  try {
    const refuseReason = returnValue.value.trim();

    if (refuseReason === "" || refuseReason === "텍스트를 입력하세요") {
      alert("반려 사유 입력이 되지 않았습니다.");
      return;
    }

    const data = {
      _id: target._id,
      processStatus: false,
      refuseReason: refuseReason,
    };

    const res = await store.dispatch("POST_TRANSFER_APPLY", data);
    console.log("서버 응답:", res);

    if (!res) {
      const response = await transferapplysms({ phonenum: target.tel, status: false });
      if (response.data.data.message == "good") {
        alert(
          "반려 사유를 전달 완료했습니다.\n\n문자 건수 : 1건\nLMS 알림 : 1건"
        );
        closeModal();
        window.location.reload(true);
      }
    } else {
      alert("서버에 문제가 발생하여 반려 사유를 전달하지 못했습니다.");
    }
  } catch (err) {
    console.log(err);
    alert("반려 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
  }
};

const issuingAll = ref(false);
const issueAllPending = async () => {
  if (issuingAll.value) return;
  if (rows.value.length === 0) {
    alert("처리 대기 중인 신청이 없습니다.");
    return;
  }
  if (
    !window.confirm(`처리 대기 중인 신청 ${rows.value.length}건을 모두 발급하시겠습니까?`)
  ) {
    return;
  }

  issuingAll.value = true;
  let successCount = 0;
  let failCount = 0;
  for (const item of rows.value) {
    try {
      const data = { _id: item._id, processStatus: true, refuseReason: "" };
      const response = await store.dispatch("POST_TRANSFER_APPLY", data);
      if (response) {
        const res = await transferapplysms({ phonenum: item.tel, status: true });
        if (res?.data?.data?.message === "good") {
          successCount++;
        } else {
          failCount++;
        }
      } else {
        failCount++;
      }
    } catch (err) {
      console.log(err);
      failCount++;
    }
  }
  issuingAll.value = false;

  alert(
    `전체 발급 완료: 성공 ${successCount}건${failCount ? `, 실패 ${failCount}건` : ""}`
  );
  window.location.reload(true);
};

defineExpose({ issueAllPending });

const fetchData = async () => {
  try {
    const page = currentPage.value;
    getTransferStudentsList(page);
  } catch (error) {
    console.error("Error:", error);
  }
};

watch(currentPage, fetchData);

onMounted(() => {
  getPageLength();
  getTransferStudentsList(1);
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});
</script>

<style scoped>
.apply-table {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.detail-cell {
  position: relative;
  display: inline-block;
}

.popup {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 220px;
  background: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  padding: var(--space-3) var(--space-4);
  z-index: 10;
  text-align: left;
}

.popup__row {
  margin: 0;
  padding: 2px 0;
  font-size: var(--font-small-size);
  color: var(--text-primary);
}

.mono {
  font-family: var(--font-mono-family, "JetBrains Mono", "D2Coding", monospace);
  font-size: var(--font-mono-size);
}

.btn-ghost-sm {
  height: 28px;
  padding: 0 var(--space-3);
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  cursor: pointer;
}

.btn-ghost-sm:hover {
  background: var(--bg-subtle);
}

.btn-primary-sm {
  height: 28px;
  padding: 0 var(--space-3);
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
}

.btn-primary-sm:hover {
  background: var(--color-primary-700);
}

.btn-destructive-sm {
  height: 28px;
  padding: 0 var(--space-3);
  background: transparent;
  color: #B42318;
  border: 1px solid #B42318;
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
}

.btn-destructive-sm:hover {
  background: #F8DEE1;
}

.pagination-row {
  display: flex;
  justify-content: center;
}

/* ─── 모바일 카드 리스트 ─── */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.mobile-empty {
  padding: var(--space-6) 0;
}

/* 카드 컨테이너 */
.apply-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.apply-card:hover {
  box-shadow: var(--shadow-md);
}

/* 헤더 영역 */
.apply-card__head {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: var(--space-3);
  align-items: center;
}

.apply-card__avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary-50);
  color: var(--color-primary-600);
  border-radius: var(--radius-md);
  flex-shrink: 0;
}

.apply-card__heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.apply-card__school {
  margin: 0;
  font-size: var(--font-body-strong-size);
  font-weight: 700;
  color: var(--text-primary);
  word-break: keep-all;
  line-height: 1.3;
}

.apply-card__meta {
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  flex-wrap: wrap;
}

.apply-card__dot {
  color: var(--border-strong);
}

/* 상태 칩 (처리 대기) */
.apply-card__status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
  border: 1px solid var(--color-warning-border);
  font-size: var(--font-caption-size);
  font-weight: 600;
  white-space: nowrap;
}

.apply-card__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-warning-text);
}

/* 연락처 인라인 */
.apply-card__contact {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-default);
  font-size: var(--font-small-size);
  color: var(--text-secondary);
}

.apply-card__contact-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.apply-card__contact-icon {
  color: var(--text-tertiary);
}

/* 신청 사유 (quote 스타일) */
.apply-card__reason {
  padding: var(--space-3);
  background: var(--bg-canvas);
  border-left: 3px solid var(--color-primary-300);
  border-radius: var(--radius-sm);
}

.apply-card__reason-label {
  font-size: var(--font-caption-size);
  font-weight: 600;
  color: var(--text-tertiary);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.apply-card__reason-text {
  margin: 0;
  font-size: var(--font-small-size);
  line-height: 1.5;
  color: var(--text-primary);
  word-break: keep-all;
}

/* 칩 (학생 N건 / 기존 처리 N건) */
.apply-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.apply-card__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 var(--space-2);
  background: var(--bg-subtle);
  color: var(--text-secondary);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  font-size: var(--font-caption-size);
  font-weight: 500;
}

.apply-card__chip--button {
  background: var(--bg-surface-raised);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.apply-card__chip--button:hover {
  background: var(--color-primary-50);
  border-color: var(--color-primary-300);
  color: var(--color-primary-700);
}

/* 액션 버튼 (발급 / 반려) */
.apply-card__footer {
  display: flex;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-default);
}

.apply-card__action {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  font-weight: 600;
  cursor: pointer;
  line-height: 1;
  white-space: nowrap;
  transition: background 0.15s ease, transform 0.05s ease;
}

.apply-card__action:active {
  transform: translateY(1px);
}

.apply-card__action--primary {
  flex: 2 1 0;
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  border: none;
}

.apply-card__action--primary:hover {
  background: var(--color-primary-700);
}

.apply-card__action--reject {
  background: var(--bg-surface-raised);
  color: #B42318;
  border: 1px solid #B42318;
}

.apply-card__action--reject:hover {
  background: var(--color-error-bg);
}

.popup--mobile {
  left: auto;
  right: 0;
  transform: none;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(20, 24, 32, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-panel {
  width: 480px;
  max-width: calc(100vw - var(--space-8));
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
}

.modal-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-6);
  border-bottom: 1px solid var(--border-default);
}

.modal-panel__title {
  margin: 0;
  font-size: var(--font-h3-size);
  line-height: var(--font-h3-line);
  font-weight: var(--font-h3-weight);
  color: var(--text-primary);
}

.modal-close {
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  font-size: 14px;
  cursor: pointer;
  padding: var(--space-1);
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-panel__body {
  padding: var(--space-6);
}

.form-label {
  display: block;
  font-size: var(--font-body-strong-size);
  font-weight: var(--font-body-strong-weight);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.required {
  margin-left: var(--space-1);
  font-size: var(--font-caption-size);
  font-weight: 400;
  color: #B42318;
}

.modal-textarea {
  width: 100%;
  height: 140px;
  resize: vertical;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-surface-raised);
  padding: var(--space-3);
  font-size: var(--font-body-size);
  line-height: var(--font-body-line);
  color: var(--text-primary);
  outline: none;
}

.modal-textarea:focus {
  border-color: var(--color-primary-600);
}

.modal-helper {
  margin: var(--space-1) 0 0;
  font-size: var(--font-small-size);
  color: var(--text-tertiary);
}

.modal-panel__footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--border-default);
}

.btn-secondary {
  height: 36px;
  padding: 0 var(--space-4);
  background: var(--bg-surface-raised);
  color: var(--text-primary);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--font-body-size);
  cursor: pointer;
}

.btn-secondary:hover {
  background: var(--bg-subtle);
}

.btn-destructive {
  height: 36px;
  padding: 0 var(--space-4);
  background: #B42318;
  color: #FFFFFF;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-body-size);
  font-weight: 600;
  cursor: pointer;
}

.btn-destructive:hover {
  background: #8E1A12;
}
</style>
